import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import { Link, useNavigate } from '@rocketlane/rocket-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Space } from 'antd';
import React, { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import { UserAPI } from 'api/user';
import { Form } from 'components/atoms/form';
import { Container, Heading, LinkText, UserInfo } from 'components/atoms/form-elements';
import { Layout } from 'components/atoms/layout';
import { PageLoading } from 'components/atoms/loading';
import { Button } from 'components/facade/button';
import { Input } from 'components/facade/input';
import { toastService } from 'components/molecules/toast';
import { TokenErrorStatus } from 'components/organisms/token-error/constant';
import { RoutePaths } from 'constant';
import { useQueryParams } from 'hooks/use-query-params';
import { useTranslation } from 'hooks/use-translation';
import { CompanyLogo } from 'routes/auth/common';
import { useDismissBootSkeleton } from 'utils/dismiss-boot-skeleton';
import { getPasswordFormErrorMessage } from 'utils/error';
import { getUserLabel } from 'utils/get-user-label';
import { getTokenMeta } from 'utils/token';
const TokenError = /*#__PURE__*/ React.lazy(()=>import('components/organisms/token-error/token-error').then((d)=>({
            default: d.TokenError
        })));
var PasswordsFields = /*#__PURE__*/ function(PasswordsFields) {
    PasswordsFields["New"] = "newPassword";
    PasswordsFields["Confirm"] = "confirmPassword";
    return PasswordsFields;
}(PasswordsFields || {});
export const ResetPasswordPage = ()=>{
    // Public route — dismiss the static #rl-boot-skeleton overlay from
    // public/index.html so it doesn't bleed through this page. See Login.
    useDismissBootSkeleton();
    const history = useNavigate();
    const params = useQueryParams();
    const urlToken = params.get('token') || '';
    const { data, isLoading } = useQuery([
        'get-details-from-token',
        urlToken
    ], ()=>UserAPI.getDetailsFromToken(urlToken), {
        retry: false,
        refetchOnWindowFocus: false
    });
    const { isExpired, decoded } = getTokenMeta(urlToken);
    const updateProfile = (values)=>{
        return UserAPI.resetPassword({
            newPassword: values["newPassword"] || '',
            confirmPassword: values["confirmPassword"] || '',
            token: urlToken || ''
        }).then(()=>{
            history(RoutePaths.login);
        }).catch((error)=>Promise.reject(error));
    };
    const passwordMatchText = useTranslation('form.validations.password_match');
    const errorTitle = useTranslation('error.generic.title');
    const errorMessageFallback = useTranslation('rte.error');
    const passwordValidationUnified = useTranslation('password.validation.unified');
    const { mutateAsync: sendResetMail, isLoading: resetMailSending } = useMutation(updateProfile);
    const onFinish = (values)=>{
        sendResetMail(values).catch((error)=>{
            const { primary, secondary } = getPasswordFormErrorMessage(error, passwordValidationUnified, errorMessageFallback);
            toastService({
                title: errorTitle,
                message: primary
            }).error();
            if (secondary != null) {
                toastService({
                    title: errorTitle,
                    message: secondary
                }).error();
            }
        });
    };
    const pwdPlaceholder = useTranslation('password.placeholder');
    if (decoded && isExpired) {
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
            children: /*#__PURE__*/ _jsx(TokenError, {
                status: TokenErrorStatus.Expired,
                email: decoded.emailId,
                resendEmail: ()=>UserAPI.triggerResetPasswordEmail(decoded.emailId),
                linkType: decoded.linkType
            })
        });
    }
    if (!decoded) {
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
            children: /*#__PURE__*/ _jsx(TokenError, {
                status: TokenErrorStatus.Invalid
            })
        });
    }
    return isLoading ? /*#__PURE__*/ _jsx(PageLoading, {}) : /*#__PURE__*/ _jsx(Layout, {
        children: /*#__PURE__*/ _jsx(Container, {
            children: /*#__PURE__*/ _jsxs(Space, {
                direction: "vertical",
                size: 16,
                children: [
                    data ? /*#__PURE__*/ _jsx(CompanyLogo, {
                        data: data === null || data === void 0 ? void 0 : data.sessionPayload.user.company
                    }) : null,
                    /*#__PURE__*/ _jsx(Heading, {
                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: "reset_password.page_title",
                            values: {
                                user: (data === null || data === void 0 ? void 0 : data.sessionPayload.user) ? getUserLabel(data === null || data === void 0 ? void 0 : data.sessionPayload.user) : ''
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Form, {
                        layout: "vertical",
                        name: "reset-password",
                        onFinish: onFinish,
                        children: [
                            /*#__PURE__*/ _jsx(Form.Item, {
                                name: "newPassword",
                                rules: [
                                    {
                                        required: true,
                                        message: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "form.validations.new_password"
                                        })
                                    },
                                    {
                                        min: 11,
                                        message: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "password.help"
                                        }),
                                        validateTrigger: 'onBlur'
                                    }
                                ],
                                label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "new_password"
                                }),
                                children: /*#__PURE__*/ _jsx(Input, {
                                    autoComplete: "new-password",
                                    type: "password",
                                    placeholder: pwdPlaceholder,
                                    name: "new-password"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Form.Item, {
                                name: "confirmPassword",
                                label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "confirm_password"
                                }),
                                rules: [
                                    {
                                        required: true,
                                        message: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "form.validations.confirm_password"
                                        })
                                    },
                                    ({ getFieldValue })=>({
                                            validator (_, value) {
                                                if (!value || getFieldValue('newPassword') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(passwordMatchText);
                                            }
                                        })
                                ],
                                children: /*#__PURE__*/ _jsx(Input, {
                                    autoComplete: "confirm-password",
                                    type: "password",
                                    placeholder: pwdPlaceholder,
                                    name: "confirm-password"
                                })
                            }),
                            /*#__PURE__*/ _jsx(Form.Item, {
                                children: /*#__PURE__*/ _jsx(Button, {
                                    htmlType: "submit",
                                    loading: resetMailSending,
                                    type: "primary",
                                    icon: /*#__PURE__*/ _jsx(ArrowRight16, {}),
                                    iconPlacement: "right",
                                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                        id: "reset_password.cta"
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(UserInfo, {
                                children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "reset_password.cta_back",
                                    values: {
                                        a: (chunks)=>/*#__PURE__*/ _jsx(Link, {
                                                to: RoutePaths.login,
                                                children: /*#__PURE__*/ _jsx(LinkText, {
                                                    children: chunks
                                                })
                                            })
                                    }
                                })
                            })
                        ]
                    })
                ]
            })
        })
    });
};
